import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Users, Stethoscope, Pill, FlaskConical, CalendarDays, ShieldCheck, LogOut, Inbox, Palette, UserCircle2, HelpCircle, MessageSquareWarning, MessageSquare } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { ThemePickerModal } from "@/components/ThemeButton";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["admin", "organizer", "doctor", "nurse"] },
  { to: "/patients", icon: Users, label: "Patients", roles: ["admin", "organizer", "doctor", "nurse"] },
  { to: "/regimens", icon: FlaskConical, label: "Regimens", roles: ["admin", "organizer", "doctor", "nurse"] },
  { to: "/drugs", icon: Pill, label: "Drug Library", roles: ["admin", "organizer", "doctor", "nurse"] },
  { to: "/approvals", icon: Inbox, label: "Approvals", roles: ["admin", "organizer", "doctor", "nurse"] },
  { to: "/requests", icon: MessageSquareWarning, label: "Patient Requests", roles: ["admin", "organizer", "doctor", "nurse"] },
  { to: "/messages", icon: MessageSquare, label: "Messages", roles: ["admin", "organizer", "doctor", "nurse"] },
  { to: "/providers", icon: Stethoscope, label: "Providers", roles: ["admin", "organizer", "doctor", "nurse"] },
  { to: "/help", icon: HelpCircle, label: "Help Center", roles: ["admin", "organizer", "doctor", "nurse"] },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { current } = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const nav = useNavigate();
  if (!user) return null;
  const items = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-stone-50">
      <aside className="w-64 shrink-0 border-r border-stone-200 bg-white flex flex-col" data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-stone-100 text-brand-700">
          <Logo size={36} withWordmark subtitle="Chemotherapy Treatment Companion" />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/dashboard"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-brand-50 text-brand-700" : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                }`
              }
            >
              <n.icon className="w-4 h-4" />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-stone-100">
          <NavLink
            to="/profile"
            data-testid="sidebar-profile-link"
            className={({ isActive }) =>
              `w-full px-3 py-2 mb-2 flex items-center gap-3 rounded-lg transition-colors ${
                isActive ? "bg-brand-50" : "hover:bg-stone-50"
              }`
            }
          >
            <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 font-display font-bold flex items-center justify-center">
              {user.full_name?.[0] || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-stone-900 truncate">{user.full_name}</div>
              <div className="text-xs text-stone-500 capitalize flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />{user.role}
              </div>
            </div>
            <UserCircle2 className="w-4 h-4 text-stone-400" />
          </NavLink>
          <button
            data-testid="sidebar-theme-btn"
            onClick={() => setPickerOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-stone-600 hover:bg-stone-50"
          >
            <Palette className="w-4 h-4" />
            <span className="flex-1 text-left">Theme</span>
            <span className="text-xs text-stone-400">{current?.name}</span>
          </button>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); nav("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-stone-600 hover:bg-stone-50"
          >
            <LogOut className="w-4 h-4" />Sign out
          </button>
        </div>
      </aside>
      <main className="hero-gradient flex-1 min-w-0 overflow-x-auto">
        <Outlet />
      </main>
      {pickerOpen && <ThemePickerModal onClose={() => setPickerOpen(false)} />}
    </div>
  );
}
